import { z } from 'zod';

/**
 * Тренд = цепочка шагов, собирается в админке без кода.
 * Генерирующие шаги (картинка, видео, песня): модель + варианты (подпись + промпт). Один вариант = один кадр.
 * Следующий шаг берёт кадры предыдущего генерирующего: каждый отдельно (each) или все сразу (all).
 * Шаг «контроль» стоит сразу после генерирующего и проверяет его кадры: сходство лица и брак (vision-модель);
 * брак перегенерируется за наш счёт.
 */

export const TREND_SECTIONS = [
  { id: 'self', label: '✨ Для себя' },
  { id: 'gifts', label: '🎁 Подарки' },
  { id: 'business', label: '💼 Для дела' },
  { id: 'kids', label: '🧸 Дети, питомцы' },
  { id: 'tryon', label: '👗 Примерка' },
  { id: 'songs', label: '🎵 Песни' },
] as const;
export type TrendSection = (typeof TREND_SECTIONS)[number]['id'];
export const trendSectionSchema = z.enum(TREND_SECTIONS.map((s) => s.id) as [TrendSection, ...TrendSection[]]);

export const TREND_KINDS = ['photo', 'video', 'song', 'stickers'] as const;
export type TrendKind = (typeof TREND_KINDS)[number];

export const TREND_STATUSES = ['draft', 'published', 'disabled'] as const;
export type TrendStatus = (typeof TREND_STATUSES)[number];

export const TREND_TAGS = ['new', 'top'] as const;
export const TREND_TAG_LABELS: Record<(typeof TREND_TAGS)[number], string> = { new: 'новинка', top: 'топ недели' };

/** Модели, доступные в конструкторе. Цены — в настройках (админка → Провайдеры). */
export const IMAGE_MODELS = [{ id: 'nano-banana-2', label: 'Nano Banana 2', maxRefs: 14 }] as const;
export type ImageModel = (typeof IMAGE_MODELS)[number]['id'];
export const VIDEO_MODELS = [{ id: 'seedance-2', label: 'Seedance 2.0', maxRefs: 9 }] as const;
export type VideoModel = (typeof VIDEO_MODELS)[number]['id'];
export const SONG_MODELS = [{ id: 'suno-v6', label: 'Suno V6' }] as const;
export type SongModel = (typeof SONG_MODELS)[number]['id'];

export const RESOLUTIONS = ['1K', '2K', '4K'] as const;
export type Resolution = (typeof RESOLUTIONS)[number];
export const VIDEO_RESOLUTIONS = ['480p', '720p', '1080p'] as const;
export type VideoResolution = (typeof VIDEO_RESOLUTIONS)[number];

export const TREND_ASPECT_RATIOS = ['9:16', '1:1', '4:5', '3:4', '2:3', '16:9'] as const;
export const VIDEO_ASPECT_RATIOS = ['9:16', '1:1', '16:9', '3:4', '4:3'] as const;

export const MAX_TREND_FRAMES = 24;
/** Кадров одного видео-шага: видео дорогие. */
export const MAX_VIDEO_FRAMES = 6;
/** Стикеров в паке при создании через Bot API — не больше 50. */
export const MAX_STICKERS = 50;
/** Модель вырезания фона для стикеров. */
export const CUTOUT_MODEL = 'recraft-remove-bg';

const variantSchema = z.object({
  /** Подпись кадра в результате: «Рыцарь», «1920-е». */
  label: z.string().trim().max(30).default(''),
  /** Промпт. Можно подставлять поля входа: {{name}}. */
  prompt: z.string().trim().min(3).max(4000),
});

const stepBase = {
  id: z.string().min(1).max(20),
  title: z.string().max(60).default(''),
};
const refs = z.enum(['user', 'previous', 'none']);
const fanout = z.enum(['each', 'all']);
/** Сколько кадров шага должно получиться, иначе генерация — провал с возвратом. 0 — все. */
const minSuccess = z.number().int().min(0).max(MAX_TREND_FRAMES).default(0);

export const imageStepSchema = z.object({
  ...stepBase,
  type: z.literal('image'),
  model: z.enum(IMAGE_MODELS.map((m) => m.id) as [ImageModel, ...ImageModel[]]),
  resolution: z.enum(RESOLUTIONS).default('1K'),
  aspectRatio: z.enum(TREND_ASPECT_RATIOS).default('9:16'),
  /** Откуда референсы: фото юзера, кадры предыдущего шага, ничего. */
  refs: refs.default('user'),
  /** Для refs=previous: каждый кадр отдельно (each) или все кадры одним запросом (all). */
  fanout: fanout.default('each'),
  variants: z.array(variantSchema).min(1).max(MAX_TREND_FRAMES),
  minSuccess,
});

export const videoStepSchema = z.object({
  ...stepBase,
  type: z.literal('video'),
  model: z.enum(VIDEO_MODELS.map((m) => m.id) as [VideoModel, ...VideoModel[]]).default('seedance-2'),
  resolution: z.enum(VIDEO_RESOLUTIONS).default('720p'),
  aspectRatio: z.enum(VIDEO_ASPECT_RATIOS).default('9:16'),
  durationSec: z.number().int().min(4).max(15).default(5),
  /** Звук от модели (эффекты, атмосфера). */
  generateAudio: z.boolean().default(true),
  /** user — фото юзера как референсы; previous — кадр прошлого шага как первый кадр видео. */
  refs: refs.default('previous'),
  fanout: fanout.default('each'),
  variants: z.array(variantSchema).min(1).max(MAX_VIDEO_FRAMES),
  minSuccess,
});

export const songStepSchema = z.object({
  ...stepBase,
  type: z.literal('song'),
  model: z.enum(SONG_MODELS.map((m) => m.id) as [SongModel, ...SongModel[]]).default('suno-v6'),
  /** Жанр и настроение: «поп-панк, бодро». Можно {{поля}}. */
  style: z.string().trim().max(300).default(''),
  instrumental: z.boolean().default(false),
  /** Промпт варианта — о чём песня (Suno сам пишет текст). */
  variants: z.array(variantSchema).min(1).max(4),
  minSuccess,
});

export const controlStepSchema = z.object({
  ...stepBase,
  type: z.literal('control'),
  /** Порог сходства лица с фото юзера (косинус ArcFace, 0 — не проверять). Один человек обычно ≥ 0,4. */
  faceMatch: z.number().min(0).max(1).default(0.45),
  /** Проверка брака vision-моделью (лишние пальцы, поплывшее лицо, мимо промпта). */
  vision: z.boolean().default(true),
  /** Сколько раз переделать брак за наш счёт. */
  maxRegenerations: z.number().int().min(0).max(3).default(2),
});

/**
 * Стикерпак: вырезаем фон у каждого кадра прошлого шага-картинки, ужимаем до 512 px и собираем пак в Telegram.
 * Эмодзи стикера — из подписи варианта прошлого шага («😂 Смех»), нет эмодзи — 🙂.
 */
export const stickerStepSchema = z.object({
  ...stepBase,
  type: z.literal('sticker'),
  /** Название пака в Telegram. Можно {{поля}}. */
  packTitle: z.string().trim().min(1).max(64).default('Мои стикеры · Trendator'),
  minSuccess,
});

export const trendStepSchema = z.discriminatedUnion('type', [imageStepSchema, videoStepSchema, songStepSchema, controlStepSchema, stickerStepSchema]);
export type TrendStep = z.infer<typeof trendStepSchema>;
export type ImageStep = z.infer<typeof imageStepSchema>;
export type VideoStep = z.infer<typeof videoStepSchema>;
export type SongStep = z.infer<typeof songStepSchema>;
export type ControlStep = z.infer<typeof controlStepSchema>;
export type StickerStep = z.infer<typeof stickerStepSchema>;
export type GenStep = ImageStep | VideoStep | SongStep | StickerStep;
export type FrameKind = 'image' | 'video' | 'song' | 'cutout';
/** Что получит юзер. */
export type OutputKind = 'image' | 'video' | 'song' | 'stickers';

/** Шаг с вариантами-промптами (у стикеров вариантов нет — кадры берутся с прошлого шага). */
export const variantsOf = (s: TrendStep): { label: string; prompt: string }[] => (s.type === 'control' || s.type === 'sticker' ? [] : s.variants);

export const isGenStep = (s: TrendStep | undefined): s is GenStep => !!s && s.type !== 'control';

/** Текстовое поле входа: «Имя друга», «Чем он знаменит». В промптах — {{id}}. */
export const trendFieldSchema = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]{0,19}$/, 'id поля: латиница, цифры, _'),
  label: z.string().trim().min(1).max(40),
  placeholder: z.string().trim().max(80).default(''),
  maxLength: z.number().int().min(1).max(300).default(60),
  required: z.boolean().default(true),
});
export type TrendField = z.infer<typeof trendFieldSchema>;

const DEFAULT_HINT = 'Одно фото, где хорошо видно лицо';

export const trendDefinitionSchema = z.object({
  inputs: z
    .object({
      /** Сколько фото юзер загружает (если не берёт двойника). */
      photos: z.object({ min: z.number().int().min(0).max(4), max: z.number().int().min(0).max(4) }).default({ min: 1, max: 1 }),
      photoHint: z.string().max(120).default(DEFAULT_HINT),
      /** Можно взять готового двойника вместо фото. */
      twin: z.boolean().default(true),
      fields: z.array(trendFieldSchema).max(5).default([]),
      /** Дуо: нужен друг — он даёт согласие и селфи в боте, его лицо идёт в кадр вторым. */
      duo: z.boolean().default(false),
      /** Можно фото детей (раздел «Дети, питомцы»). Иначе лицо младше 18 на входе — отказ. */
      minors: z.boolean().default(false),
    })
    .default({ photos: { min: 1, max: 1 }, photoHint: DEFAULT_HINT, twin: true, fields: [], duo: false, minors: false }),
  steps: z.array(trendStepSchema).min(1).max(8),
});
export type TrendDefinition = z.infer<typeof trendDefinitionSchema>;

export interface PublishIssue {
  step?: string;
  message: string;
}

/** Индекс предыдущего генерирующего шага (контроль пропускаем), −1 — нет. */
export function prevGenIndex(def: TrendDefinition, i: number): number {
  for (let j = i - 1; j >= 0; j--) if (isGenStep(def.steps[j])) return j;
  return -1;
}

/** Индекс следующего генерирующего шага после i, −1 — нет. */
export function nextGenIndex(def: TrendDefinition, i: number): number {
  for (let j = i + 1; j < def.steps.length; j++) if (isGenStep(def.steps[j])) return j;
  return -1;
}

/** Шаг контроля, проверяющий кадры шага i (стоит сразу за ним). */
export function controlFor(def: TrendDefinition, i: number): ControlStep | null {
  const s = def.steps[i + 1];
  return s?.type === 'control' ? s : null;
}

const PLACEHOLDER = /\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g;

export function placeholdersOf(text: string): string[] {
  return [...text.matchAll(PLACEHOLDER)].map((m) => m[1]!);
}

/** Подставить поля входа в текст. Значения обрезаем и убираем фигурные скобки. */
export function fillTemplate(text: string, values: Record<string, string>): string {
  return text.replace(PLACEHOLDER, (_, id: string) => (values[id] ?? '').replace(/[{}]/g, '').trim());
}

/** Что мешает опубликовать (проверяем и в админке, и на сервере). */
export function publishIssues(def: TrendDefinition): PublishIssue[] {
  const issues: PublishIssue[] = [];
  const ids = new Set<string>();
  const fieldIds = new Set(def.inputs.fields.map((f) => f.id));
  if (fieldIds.size !== def.inputs.fields.length) issues.push({ message: 'Повторяется id поля входа' });
  const usesFace = def.inputs.photos.max > 0 || def.inputs.twin;

  def.steps.forEach((s, i) => {
    if (ids.has(s.id)) issues.push({ step: s.id, message: `Повторяется id шага «${s.id}»` });
    ids.add(s.id);

    if (s.type === 'control') {
      const prev = def.steps[i - 1];
      if (!isGenStep(prev)) issues.push({ step: s.id, message: 'Контроль ставится сразу после картинки, видео или песни' });
      else if (prev.type === 'song') issues.push({ step: s.id, message: 'Песни контроль не проверяет — убери шаг' });
      else if (prev.type === 'sticker') issues.push({ step: s.id, message: 'Контроль ставится перед стикерами, на кадры-картинки' });
      if (s.faceMatch > 0 && !usesFace) issues.push({ step: s.id, message: 'Сверка лица без фото юзера невозможна — поставь порог 0' });
      return;
    }

    const prevIdx = prevGenIndex(def, i);
    if (s.type === 'sticker') {
      for (const p of placeholdersOf(s.packTitle)) {
        if (!fieldIds.has(p)) issues.push({ step: s.id, message: `В названии пака {{${p}}}, а такого поля во входе нет` });
      }
      if (prevIdx < 0 || def.steps[prevIdx]!.type !== 'image') issues.push({ step: s.id, message: 'Стикеры делаются из кадров-картинок прошлого шага' });
      if (lastGenIndex(def) !== i) issues.push({ step: s.id, message: 'Стикеры — последний шаг' });
      if (framesOfStep(def, i) > MAX_STICKERS) issues.push({ step: s.id, message: `Больше ${MAX_STICKERS} стикеров в пак не собрать` });
      if (s.minSuccess > framesOfStep(def, i)) issues.push({ step: s.id, message: 'Порог успешных кадров больше числа кадров' });
      return;
    }

    const texts = [...s.variants.flatMap((v) => [v.prompt, v.label]), s.type === 'song' ? s.style : ''];
    for (const p of texts.flatMap(placeholdersOf)) {
      if (!fieldIds.has(p)) issues.push({ step: s.id, message: `В промпте {{${p}}}, а такого поля во входе нет` });
    }

    if (s.type === 'song') return;
    if (s.refs === 'previous') {
      if (prevIdx < 0) issues.push({ step: s.id, message: 'Первый шаг не может брать кадры предыдущего' });
      else if (def.steps[prevIdx]!.type !== 'image') issues.push({ step: s.id, message: 'Брать можно только кадры-картинки прошлого шага' });
    }
    if (s.refs === 'user' && def.inputs.photos.max === 0 && !def.inputs.twin) {
      issues.push({ step: s.id, message: 'Шаг берёт фото юзера, но во входе 0 фото и нет двойника' });
    }
    if (s.minSuccess > framesOfStep(def, i)) issues.push({ step: s.id, message: 'Порог успешных кадров больше числа кадров' });
  });
  if (!def.steps.some(isGenStep)) issues.push({ message: 'Нужен хотя бы один шаг картинка, видео или песня' });
  if (def.inputs.duo && def.inputs.photos.max === 0 && !def.inputs.twin) issues.push({ message: 'Дуо: нужно лицо пригласившего — фото или двойник' });
  if (def.inputs.duo && !def.steps.some((s) => (s.type === 'image' || s.type === 'video') && s.refs === 'user')) {
    issues.push({ message: 'Дуо: хотя бы один шаг должен брать фото юзера — туда же пойдёт селфи друга' });
  }
  if (def.inputs.photos.min > def.inputs.photos.max) issues.push({ message: 'Минимум фото больше максимума' });
  if (totalFrames(def) > MAX_TREND_FRAMES * 2) issues.push({ message: 'Слишком много кадров в тренде' });
  return issues;
}

/** Сколько кадров даст шаг i (для refs=previous+each умножается на кадры предыдущего). Контроль — 0. */
export function framesOfStep(def: TrendDefinition, i: number): number {
  const s = def.steps[i];
  if (!isGenStep(s)) return 0;
  const prev = prevGenIndex(def, i);
  if (s.type === 'sticker') return prev >= 0 ? framesOfStep(def, prev) : 0;
  const sources = s.type !== 'song' && s.refs === 'previous' && s.fanout === 'each' && prev >= 0 ? framesOfStep(def, prev) : 1;
  return sources * s.variants.length;
}

export function totalFrames(def: TrendDefinition): number {
  return def.steps.reduce((sum, _, i) => sum + framesOfStep(def, i), 0);
}

/** Индекс последнего генерирующего шага — его кадры уходят юзеру. */
export function lastGenIndex(def: TrendDefinition): number {
  for (let j = def.steps.length - 1; j >= 0; j--) if (isGenStep(def.steps[j])) return j;
  return -1;
}

/** Кадров в выдаче (последний генерирующий шаг). */
export function outputFrames(def: TrendDefinition): number {
  const i = lastGenIndex(def);
  return i < 0 ? 0 : framesOfStep(def, i);
}

/** Что получит юзер: фото, видео, песня или стикерпак. */
export function outputKind(def: TrendDefinition): OutputKind {
  const s = def.steps[lastGenIndex(def)];
  return s?.type === 'video' ? 'video' : s?.type === 'song' ? 'song' : s?.type === 'sticker' ? 'stickers' : 'image';
}

/** Тип тренда (каталог, генерация) по выдаче. */
export function trendKindOf(def: TrendDefinition): TrendKind {
  const o = outputKind(def);
  return o === 'image' ? 'photo' : o;
}

/** Оценка времени, сек: шаги идут по очереди, кадры шага параллельно. */
export function estimateEtaSec(def: TrendDefinition): number {
  return (
    def.steps.reduce((sum, s) => {
      if (s.type === 'image') return sum + 20;
      if (s.type === 'video') return sum + 90 + s.durationSec * 6;
      if (s.type === 'song') return sum + 90;
      if (s.type === 'sticker') return sum + 30;
      return sum + 10;
    }, 0) + 10
  );
}

// ---------- Экономика (настройки в админке) ----------

export interface Economics {
  /** Курс $ → ₽. */
  usdRub: number;
  /** Эквайринг, % от цены. */
  acquiringPct: number;
  /** Запас на брак, % от себестоимости. */
  defectReservePct: number;
}

export const DEFAULT_ECONOMICS: Economics = { usdRub: 95, acquiringPct: 9, defectReservePct: 5 };

/**
 * Цена модели у провайдера, $:
 * картинки — за кадр по разрешению (1K/2K/4K), видео — за секунду по разрешению (480p/720p/1080p),
 * песни — за задачу (track), проверка качества — за проверку (check).
 */
export type ModelCosts = Record<string, Partial<Record<'kie' | 'fal', Partial<Record<string, number>>>>>;

/**
 * Оценки на октябрь 2026 (docs/economics.md). Видео kie — ~$0.057/с за 720p; fal — по их прайсу;
 * Suno и проверка — прикидка, уточнить по счёту kie.ai.
 */
export const DEFAULT_MODEL_COSTS: ModelCosts = {
  'nano-banana-2': {
    kie: { '1K': 0.02, '2K': 0.03, '4K': 0.045 },
    fal: { '1K': 0.06, '2K': 0.09, '4K': 0.135 },
  },
  'seedance-2': {
    kie: { '480p': 0.035, '720p': 0.057, '1080p': 0.13 },
    fal: { '480p': 0.15, '720p': 0.3034, '1080p': 0.682 },
  },
  'suno-v6': { kie: { track: 0.06 } },
  'qc-vision': { kie: { check: 0.002 } },
  // Вырезание фона (recraft/remove-background на kie.ai) — прикидка, уточнить по счёту.
  [CUTOUT_MODEL]: { kie: { image: 0.01 } },
};

export interface TrendEconomics {
  costRub: number;
  reserveRub: number;
  acquiringRub: number;
  marginRub: number;
  marginPct: number;
  perStep: { id: string; costRub: number; frames: number }[];
}

/** $ за один кадр шага у провайдера. */
export function frameCostUsd(s: GenStep, costs: ModelCosts, provider: 'kie' | 'fal'): number {
  const model = s.type === 'sticker' ? CUTOUT_MODEL : s.model;
  const c = costs[model]?.[provider] ?? costs[model]?.kie ?? {};
  if (s.type === 'image') return c[s.resolution] ?? 0;
  if (s.type === 'video') return (c[s.resolution] ?? 0) * s.durationSec;
  if (s.type === 'sticker') return (costs[CUTOUT_MODEL]?.[provider] ?? costs[CUTOUT_MODEL]?.kie ?? {}).image ?? 0;
  return c.track ?? 0;
}

/** Себестоимость и маржа тренда по основному провайдеру. */
export function trendEconomics(
  def: TrendDefinition,
  price: number,
  eco: Economics = DEFAULT_ECONOMICS,
  costs: ModelCosts = DEFAULT_MODEL_COSTS,
  provider: 'kie' | 'fal' = 'kie',
): TrendEconomics {
  const perStep = def.steps.map((s, i) => {
    let frames = framesOfStep(def, i);
    let usd = 0;
    if (isGenStep(s)) usd = frameCostUsd(s, costs, provider) * frames;
    else {
      // Контроль: проверка каждого кадра предыдущего шага.
      frames = framesOfStep(def, i - 1);
      usd = s.vision ? (costs['qc-vision']?.kie?.check ?? 0) * frames : 0;
    }
    return { id: s.id, frames, costRub: Math.round(usd * eco.usdRub * 100) / 100 };
  });
  const cost = perStep.reduce((a, s) => a + s.costRub, 0);
  const costRub = Math.round(cost);
  const reserveRub = Math.round((cost * eco.defectReservePct) / 100);
  const acquiringRub = Math.round((price * eco.acquiringPct) / 100);
  const marginRub = price - costRub - reserveRub - acquiringRub;
  return {
    costRub,
    reserveRub,
    acquiringRub,
    marginRub,
    marginPct: price > 0 ? Math.round((marginRub / price) * 100) : 0,
    perStep,
  };
}

// ---------- API мини-аппа ----------

export const trendCardSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  subtitle: z.string(),
  kind: z.enum(TREND_KINDS),
  section: trendSectionSchema,
  tags: z.array(z.enum(TREND_TAGS)),
  price: z.number().int(),
  /** Доступна бесплатная первая генерация на этом тренде. */
  allowFree: z.boolean(),
  coverUrl: z.string().nullable(),
  coverGradient: z.string(),
  frames: z.number().int(),
  /** Генераций за 7 дней — для «растут» и мест в топе. */
  popularity: z.number().int(),
});
export type TrendCard = z.infer<typeof trendCardSchema>;

export const trendDetailSchema = trendCardSchema.extend({
  description: z.string(),
  beforeUrl: z.string().nullable(),
  photos: z.object({ min: z.number().int(), max: z.number().int() }),
  photoHint: z.string(),
  /** Можно вместо фото взять двойника. */
  twin: z.boolean().default(false),
  fields: z
    .array(z.object({ id: z.string(), label: z.string(), placeholder: z.string(), maxLength: z.number().int(), required: z.boolean() }))
    .default([]),
  /** Что придёт: фото, видео, песня, стикерпак. */
  output: z.enum(['image', 'video', 'song', 'stickers']).default('image'),
  /** Дуо: нужен друг (согласие и селфи в боте). */
  duo: z.boolean().default(false),
  /** Есть проверка качества (брак переделаем). */
  qc: z.boolean().default(false),
  etaSec: z.number().int(),
});
export type TrendDetail = z.infer<typeof trendDetailSchema>;

export const createTrendRequestSchema = z.object({
  kind: z.literal('trend'),
  trendId: z.string().uuid(),
  photoIds: z.array(z.string().uuid()).max(4).default([]),
  /** Взять готового двойника вместо фото. */
  useTwin: z.boolean().default(false),
  /** Значения текстовых полей входа. */
  fields: z.record(z.string().max(20), z.string().max(300)).default({}),
});
export type CreateTrendRequest = z.input<typeof createTrendRequestSchema>;

/** Проверить и почистить значения полей входа. null — всё ок, иначе текст ошибки. */
export function validateFields(def: TrendDefinition, values: Record<string, string>): { values: Record<string, string>; error: string | null } {
  const out: Record<string, string> = {};
  for (const f of def.inputs.fields) {
    const v = (values[f.id] ?? '').replace(/\s+/g, ' ').trim().slice(0, f.maxLength);
    if (f.required && !v) return { values: out, error: `Заполни поле «${f.label}»` };
    out[f.id] = v;
  }
  return { values: out, error: null };
}

// ---------- План кадров шага ----------

export interface ImageParams {
  model: string;
  resolution: Resolution;
  aspectRatio: string;
}
export interface VideoParams {
  model: string;
  resolution: VideoResolution;
  aspectRatio: string;
  durationSec: number;
  generateAudio: boolean;
  /** first_frame — оживляем картинку; reference — лицо/стиль с фото. */
  imageMode: 'first_frame' | 'reference';
}
export interface SongParams {
  model: string;
  style: string;
  instrumental: boolean;
  title: string;
}
export interface CutoutParams {
  model: string;
}
export type FrameParams = ImageParams | VideoParams | SongParams | CutoutParams;

export interface PlannedFrame {
  index: number;
  kind: FrameKind;
  label: string;
  prompt: string;
  params: FrameParams;
  /** Ключи S3 референсов. */
  inputKeys: string[];
}

/**
 * Кадры шага i. userKeys — фото юзера (или двойника); previous — готовые кадры предыдущего генерирующего шага;
 * values — поля входа ({{name}} в промптах).
 * refs=previous + each: по кадру на каждый готовый кадр × вариант (подпись берётся у предыдущего кадра).
 */
export function planStep(
  def: TrendDefinition,
  i: number,
  inputs: { userKeys: string[]; previous: { key: string; label: string }[]; values?: Record<string, string> },
): PlannedFrame[] {
  const s = def.steps[i];
  if (!isGenStep(s)) throw new Error(`Шаг ${i} не генерирующий`);
  const values = inputs.values ?? {};
  if (s.type === 'sticker') {
    return inputs.previous.map((p, index) => ({ index, kind: 'cutout' as const, label: p.label, prompt: '', params: { model: CUTOUT_MODEL }, inputKeys: [p.key] }));
  }
  if (s.type === 'song') {
    return s.variants.map((v, index) => ({
      index,
      kind: 'song' as const,
      label: fillTemplate(v.label, values),
      prompt: fillTemplate(v.prompt, values),
      params: { model: s.model, style: fillTemplate(s.style, values), instrumental: s.instrumental, title: fillTemplate(v.label, values) },
      inputKeys: [],
    }));
  }
  const params: ImageParams | VideoParams =
    s.type === 'image'
      ? { model: s.model, resolution: s.resolution, aspectRatio: s.aspectRatio }
      : {
          model: s.model,
          resolution: s.resolution,
          aspectRatio: s.aspectRatio,
          durationSec: s.durationSec,
          generateAudio: s.generateAudio,
          imageMode: s.refs === 'previous' ? 'first_frame' : 'reference',
        };
  const sources: { keys: string[]; label: string }[] =
    s.refs === 'user'
      ? [{ keys: inputs.userKeys, label: '' }]
      : s.refs === 'none'
        ? [{ keys: [], label: '' }]
        : s.fanout === 'all'
          ? [{ keys: inputs.previous.map((p) => p.key), label: '' }]
          : inputs.previous.map((p) => ({ keys: [p.key], label: p.label }));
  const frames: PlannedFrame[] = [];
  for (const src of sources) {
    for (const v of s.variants) {
      const vLabel = fillTemplate(v.label, values);
      const label = [src.label, s.variants.length > 1 || !src.label ? vLabel : ''].filter(Boolean).join(' · ');
      frames.push({ index: frames.length, kind: s.type, label, prompt: fillTemplate(v.prompt, values), params, inputKeys: src.keys });
    }
  }
  return frames;
}

/** Сколько кадров шага должно получиться (0 в определении = все). */
export function requiredSuccess(def: TrendDefinition, i: number, planned: number): number {
  const s = def.steps[i];
  if (!isGenStep(s) || s.minSuccess === 0) return planned;
  return Math.min(s.minSuccess, planned);
}
